"use client";
import React, { Dispatch, SetStateAction, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import Link from "next/link";
import { Locale } from "../../../i18n-config";
import Lucide from "@/base-components/Lucide";
import menuData from "../Header/menuData";
import { useDictionary } from "@/libs/DictionaryProvider";

function getNavigationText(value: unknown, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function getNavigationGroupText(value: unknown, key: string, fallback: string) {
  if (value && typeof value === "object" && key in value) {
    const label = (value as Record<string, unknown>)[key];
    return typeof label === "string" ? label : fallback;
  }

  return fallback;
}

const MobileNav = ({
  lang,
  setNavigationOpen,
}: {
  lang: Locale;
  setNavigationOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const { navigation } = useDictionary();

  return (
    <nav className="flex lg:hidden flex-col gap-2 items-start w-full mb-4 px-3 !overflow-y-scroll">
      {menuData.map((menuItem) => {
        const submenuId = `mobile-nav-submenu-${menuItem.id}`;
        const isSubmenuOpen = openMenuId === menuItem.id;
        const navLabel = navigation[menuItem.title];

        return (
          <div key={menuItem.path} className="w-full">
            {!menuItem.submenu ? (
              <Link
                prefetch={menuItem.path !== "tools/trade"}
                href={`/${lang}/${menuItem.path}`}
                className="w-full"
                onClick={() => {
                  setNavigationOpen(false);
                }}
              >
                <div className="flex w-full items-start gap-3 text-white font-semibold p-2 cursor-pointer rounded-lg hover:bg-white/5">
                  <span className="shrink-0">
                    <Lucide icon={menuItem.icon} />
                  </span>
                  <span className="block min-w-0 leading-tight break-words">
                    {getNavigationText(navLabel, menuItem.titleClosed)}
                  </span>
                </div>
              </Link>
            ) : (
              <div className="w-full">
                <button
                  type="button"
                  aria-expanded={isSubmenuOpen}
                  aria-controls={submenuId}
                  className="flex w-full items-start justify-between gap-3 text-white my-0.5 font-semibold p-2 cursor-pointer rounded-lg hover:bg-white/5"
                  onClick={() =>
                    setOpenMenuId((current) =>
                      current === menuItem.id ? null : menuItem.id,
                    )
                  }
                >
                  <span className="flex min-w-0 items-start gap-3">
                    <span className="shrink-0">
                      <Lucide icon={menuItem.icon} />
                    </span>
                    <span className="block min-w-0 leading-tight break-words">
                      {getNavigationGroupText(
                        navLabel,
                        "title",
                        menuItem.titleClosed,
                      )}
                    </span>
                  </span>
                  {isSubmenuOpen ? (
                    <ChevronUp className="h-4 w-4 shrink-0" />
                  ) : (
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  )}
                </button>
                {isSubmenuOpen && (
                  <div id={submenuId} className="mt-1 flex flex-col gap-1 pl-6">
                    {menuItem.submenu.map((item) => (
                      <Link
                        href={`/${lang}/${item.path}`}
                        className="w-full"
                        key={`${menuItem.path}-${item.path}`}
                        onClick={() => {
                          setNavigationOpen(false);
                        }}
                      >
                        <div className="flex w-full items-start gap-3 text-white font-semibold p-2 cursor-pointer rounded-lg hover:bg-indigo-800">
                          <span className="shrink-0">
                            <Lucide
                              icon={item.icon ? item.icon : "CircleHelp"}
                            />
                          </span>
                          <span className="block min-w-0 leading-tight break-words">
                            {getNavigationGroupText(
                              navLabel,
                              item.title,
                              item.titleClosed,
                            )}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

export default MobileNav;
